import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { dealerApi } from "../lib/api";
import { dateTime, tzs } from "../lib/format";
import { PageHead } from "../components/PageHead";

interface StatementLine {
  journal_ref: string;
  occurred_at: string | null;
  type: string;
  description: string;
  debit_minor: number;
  credit_minor: number;
  balance_minor: number;
  order_ref: string | null;
}

interface StatementPage {
  opening_balance_minor: number;
  closing_balance_minor: number;
  currency: string;
  lines: StatementLine[];
  page: { next_cursor: string | null; has_more: boolean };
}

export function Statement() {
  const [cursor, setCursor] = useState<string | null>(null);
  const [pages, setPages] = useState<StatementPage[]>([]);

  const appendPage = (data: StatementPage) => {
    setPages((prev) => {
      const seen = new Set(prev.flatMap((p) => p.lines.map((l) => l.journal_ref + l.occurred_at)));
      const fresh = data.lines.filter((l) => !seen.has(l.journal_ref + l.occurred_at));
      if (!fresh.length) return prev;
      return [...prev, { ...data, lines: fresh }];
    });
    setCursor(data.page.next_cursor);
  };

  const statement = useQuery({
    queryKey: ["dealer-statement", cursor],
    queryFn: () => {
      const q = cursor ? `?cursor=${encodeURIComponent(cursor)}&limit=25` : "?limit=25";
      return dealerApi.get<StatementPage>(`/superdealer/v1/wallet/statement${q}`);
    },
  });

  if (statement.isSuccess && pages.length === 0) {
    appendPage(statement.data);
  }

  const closing = statement.data?.closing_balance_minor ?? pages[pages.length - 1]?.closing_balance_minor ?? 0;
  const lines = pages.flatMap((p) => p.lines);

  return (
    <div>
      <PageHead title="Wallet statement" description="Every movement on your dealer float wallet." />

      <div className="dash-kpi-row" style={{ marginBottom: 16 }}>
        <div className="dash-kpi dash-kpi-navy">
          <span className="dash-kpi-label">Closing balance</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{tzs(closing)}</span>
          </div>
        </div>
        <div className="dash-kpi dash-kpi-teal">
          <span className="dash-kpi-label">Lines shown</span>
          <div className="dash-kpi-body">
            <span className="dash-kpi-value">{lines.length}</span>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Recent activity</h3>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-table-th">Date</th>
                <th className="admin-table-th">Type</th>
                <th className="admin-table-th">Description</th>
                <th className="admin-table-th">Debit</th>
                <th className="admin-table-th">Credit</th>
                <th className="admin-table-th">Balance</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.journal_ref}-${i}`} className="admin-table-row-hover">
                  <td className="admin-table-td ev-body-micro muted">{dateTime(l.occurred_at)}</td>
                  <td className="admin-table-td">
                    <span className="ev-badge ev-badge-blue">{l.type}</span>
                  </td>
                  <td className="admin-table-td">{l.description}</td>
                  <td className="admin-table-td">{l.debit_minor ? tzs(l.debit_minor) : "—"}</td>
                  <td className="admin-table-td">{l.credit_minor ? tzs(l.credit_minor) : "—"}</td>
                  <td className="admin-table-td">
                    <strong>{tzs(l.balance_minor)}</strong>
                  </td>
                </tr>
              ))}
              {!statement.isLoading && !lines.length && (
                <tr>
                  <td className="admin-table-td" colSpan={6}>
                    <div className="dash-table-empty">No wallet activity yet.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {statement.data?.page.has_more && (
          <div className="admin-pagination">
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              onClick={() => {
                if (statement.data) appendPage(statement.data);
              }}
              disabled={statement.isFetching}
            >
              {statement.isFetching ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
